import { useCallback, useState } from 'react'
import { App } from 'antd'
import { MindmapSurveyService } from '../services/mindmapSurveyService'

export interface UseSurveyCompletionOptions {
  mindmapId: string | undefined
  surveyId: string | undefined
  /** Called after a successful complete or reopen so the page can refetch. */
  onUpdated: () => void
}

export interface UseSurveyCompletionResult {
  complete: () => void
  reopen: () => Promise<void>
  isCompleting: boolean
  isReopening: boolean
}

/**
 * Encapsulates the complete/reopen flow for a survey: API call + user-facing
 * notifications + per-action loading state. Synthesis runs server-side and
 * arrives via the survey realtime channel — this hook does not wait for it.
 */
export function useSurveyCompletion({
  mindmapId,
  surveyId,
  onUpdated,
}: UseSurveyCompletionOptions): UseSurveyCompletionResult {
  const { notification, modal } = App.useApp()
  const [isCompleting, setIsCompleting] = useState(false)
  const [isReopening, setIsReopening] = useState(false)

  const complete = useCallback(() => {
    if (!mindmapId || !surveyId) return
    modal.confirm({
      title: 'Complete this survey?',
      content:
        'The link will stop accepting new responses, and we’ll generate AI findings from what you have. You can reopen the survey later if you want to gather more.',
      okText: 'Complete & analyze',
      cancelText: 'Cancel',
      onOk: async () => {
        setIsCompleting(true)
        const res = await MindmapSurveyService.complete(mindmapId, surveyId)
        setIsCompleting(false)

        if (res.error || !res.data) {
          const code = (res.error as { code?: string } | null)?.code
          const msg =
            code === 'NO_RESPONSES'
              ? 'Cannot complete a survey with no responses yet.'
              : code === 'SURVEY_ALREADY_CLOSED'
                ? 'This survey is already closed.'
                : 'Could not complete the survey. Please try again.'
          notification.error({ title: msg })
          // Still refetch on already-closed so the UI catches up to reality.
          if (code === 'SURVEY_ALREADY_CLOSED') onUpdated()
          return
        }

        notification.success({ title: 'Survey closed — analyzing responses…' })
        onUpdated()
      },
    })
  }, [mindmapId, surveyId, modal, notification, onUpdated])

  const reopen = useCallback(async () => {
    if (!mindmapId || !surveyId) return
    setIsReopening(true)
    const res = await MindmapSurveyService.reopen(mindmapId, surveyId)
    setIsReopening(false)

    if (res.error || !res.data) {
      notification.error({ title: 'Could not reopen the survey. Please try again.' })
      return
    }

    notification.success({ title: 'Survey reopened — accepting new responses.' })
    onUpdated()
  }, [mindmapId, surveyId, notification, onUpdated])

  return { complete, reopen, isCompleting, isReopening }
}
